import React, { useContext } from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import * as styles from "./InstallStyles.scss";
import { Paper, SkeletonLoader, Title } from "@yoast/ui-library";
import classNames from "classnames";
import { StepNavigation } from "./StepNavigation";
import { InstallContext } from "./InstallContext";
import useRequest from "../../reactHooks/useRequest";
import { ExternalLink } from "../ExternalLink";

/**
 * @typedef {import('../../../../nestjs-server/src/domains/PluginInstallation/' +
 *     'UserIntefrace/dtos/getInstallationSitesDto').GetInstallationSitesResponseDto} InstallationSites
 */

const messages = defineMessages( {
	title: {
		id: "uploadPlugin.title",
		defaultMessage: "Install and activate your product(s)!",
	},
	introductionText: {
		id: "uploadPlugin.introductionText",
		defaultMessage: "You're almost there! Now that your site is connected and your product is downloaded, it's time to install it on your site.",
	},
	stepOneTitle: {
		id: "uploadPlugin.stepOneTitle",
		defaultMessage: "Step 1: Upload the file to your site",
	},
	stepOneDescription: {
		id: "uploadPlugin.stepOneDescription",
		defaultMessage: "Log in to your site below and upload the ZIP file you just downloaded.",
	},
	siteSectionTitle: {
		id: "uploadPlugin.siteSectionTitle",
		defaultMessage: "Site",
	},
	siteSectionNoSitesMessage: {
		id: "uploadPlugin.siteSectionNoSitesMessage",
		defaultMessage: "To install your product(s), you need to connect a site. " +
			"You can go back to the previous step to do this now or connect your site later via MyYoast.",
	},
	siteSectionLoginLink: {
		id: "uploadPlugin.siteSectionLoginLink",
		defaultMessage: "Log in",
	},
	stepTwoTitle: {
		id: "uploadPlugin.stepTwoTitle",
		defaultMessage: "Step 2: Install and activate",
	},
	stepTwoDescription: {
		id: "uploadPlugin.stepTwoDescription",
		defaultMessage: "Once uploaded, click 'Install Now', then 'Activate' to start using your product(s).",
	},
	needHelpVideoTitle: {
		id: "uploadPlugin.needHelpVideo",
		defaultMessage: "Need help?",
	},
	needHelpVideoDescription: {
		id: "uploadPlugin.videoDescription",
		defaultMessage: "Watch this short step-by-step video below, or check out {installationManualLink} " +
			"to install and activate {plugin}.",
		values: {
			plugin: "your plugin",
			installationManualLink: <a target="_blank" rel="noreferrer" href={ "https://yoa.st/myyoast-installation" }>
				our detailed installation guide
			</a>,
		},
	},
} );

/**
 * Redirects the user to the upload plugin zip page on the selected site.
 *
 * @param {string} connectedSiteURL The URL of the connected site.
 *
 * @returns {string} The URL to the upload plugin zip page on the selected site.
 */
function uploadsSiteUrl( connectedSiteURL ) {
	const customerSite = connectedSiteURL.replace( /\/$/, "" );

	return customerSite + "/wp-admin/plugin-install.php?tab=upload";
}

/**
 * Renders the InstallPremiumStep component.
 * @returns {Element} The rendered component.
 */
export const InstallPremiumStep = () => {
	const { invoiceNumberParam } = useContext( InstallContext );


	const url = invoiceNumberParam ? `v1/installation/sites/${ invoiceNumberParam }` : "v1/installation/sites";
	/** @type {{data:InstallationSites|[], isLoading}} */
	const request = useRequest( url, "GET" );

	return <div className={ classNames( styles.downloadingStep, styles.ySpacing ) }>
		<Title className={ styles.textCenter }><FormattedMessage { ...messages.title } /></Title>
		<hr />
		<>
			<p>
				<FormattedMessage { ...messages.introductionText } />
			</p>
			<p className={ "yst-mt-4" }>
				<span className={ "yst-font-bold" }><FormattedMessage { ...messages.stepOneTitle } /></span><br />
				<FormattedMessage { ...messages.stepOneDescription } />
			</p>
		</>

		{ request.isLoading && <SkeletonLoader as="div">
			Placeholder content
		</SkeletonLoader> }
		<Paper className={ styles.paper }>
			<Title as={ "p" } size={ "5" } className={ "yst-mb-4" }>
				<FormattedMessage { ...messages.siteSectionTitle } />
			</Title>
			{ ( ! request?.data?.sites || request.data.sites.length === 0 ) && ( <>
				<hr />
				<p className={ classNames( "yst-mt-4", "yst-italic", "yst-text-slate-500" ) }>
					<FormattedMessage { ...messages.siteSectionNoSitesMessage } />
				</p>
			</>
			) }
			{ request.data.sites && request.data.sites.length > 0 && ( <>
				<ul className={ styles.sitesList }>
					{ request.data.sites.map( ( site ) => (
						<li key={ site.id }>
							<div>
								<span> { site.url }</span>
								<ExternalLink href={ uploadsSiteUrl( site.url ) } className={ styles.purpleLink }>
									<FormattedMessage { ...messages.siteSectionLoginLink } />
								</ExternalLink>
							</div>
						</li>
					) ) }
				</ul>
			</>
			) }
		</Paper>
		<>
			<p>
				<span className={ classNames( "yst-font-bold" ) }><FormattedMessage { ...messages.stepTwoTitle } /></span><br />
				<FormattedMessage { ...messages.stepTwoDescription } />
			</p>

			<p className={ "yst-italic" }>
				<span className={ "yst-font-bold" }><FormattedMessage { ...messages.needHelpVideoTitle } /></span><br />
				<FormattedMessage { ...messages.needHelpVideoDescription } />
			</p>

			<iframe
				title={ "Step-by-step installation guide" }
				className={ styles.videoFrame }
				width="500"
				height="250"
				src={ "https://fast.wistia.net/embed/iframe/wogh6vsr21?dnt=1&autoPlay=false" }
				allowFullScreen={ true }
				frameBorder="0"
			/>
		</>

		<hr />
		<StepNavigation />
	</div>;
};
